import { BorderDefinition, ColorModel, BorderStyle } from '@tableau/color-and-border-models';
import * as React from 'react';
import { DashboardButtonType, DashboardButtonBehaviour, DashboardServerButtonEnabledState } from '../../gen-src/preslayer/Enums';
import { KeyboardEventKey } from '../KeyboardEventKey';
import { ButtonZoneStyle } from '../styles/ButtonZoneStyle';
import { ButtonZoneTelemetryConstants } from '../telemetry/ButtonZoneTelemetryConstants';
import { ButtonZoneTelemetryInterface } from '../telemetry/ButtonZoneTelemetryInterface';
import { ButtonZoneImageComponent } from './ButtonZoneImageComponent';
import { ButtonZoneTextComponent } from './ButtonZoneTextComponent';

const DefaultHoverBorderWidth = 1;
export const TextButtonHoverOverlayColorDark = ColorModel.fromColorModelPlusAlpha(ColorModel.BlackHoverColor, 0.4);
export const TextButtonHoverOverlayColorLight = ColorModel.fromColorModelPlusAlpha(ColorModel.WhiteHoverColor, 0.4);
export const HexGrayscaleBackgroundColor = '#e5e5e5';
export const HexGrayscaleBorderColor = '#808080';
export const ButtonZoneComponentTestId = 'tab-custom-button';

export interface ButtonZoneTextContent {
  effectiveCaption: string;
  captionTextFont: string;
  captionTextColor: string;
  captionFontSize: number;
  captionFontIsBold: boolean;
  captionFontIsItalic: boolean;
  captionFontIsUnderlined: boolean;
  handleFontLoaded: () => void;
}

export interface ButtonZoneImageContent {
  imageUrl: string;
  encodedImage: string;
  encodedSVGImage: string;
  imageHeight: number | string;
  imageWidth: number | string;
  onImageLoadError?: () => void;
}

export interface ButtonZoneProps {
  backgroundColor: string;
  border: BorderDefinition;
  buttonType: DashboardButtonType;
  buttonBehaviour: DashboardButtonBehaviour;
  isAuthoring: boolean;
  isCurrentSheetStoryboard: boolean;
  enabledState: DashboardServerButtonEnabledState;
  buttonTooltip: string;
  contentWidth: number | string;
  contentHeight: number | string;
  textContent: ButtonZoneTextContent;
  imageContent: ButtonZoneImageContent;
  buttonClickAction: () => void;
  handleAuthoringDoubleClick: () => void;
  handlePermissionsError: () => void;
  buttonZoneTelemetry: ButtonZoneTelemetryInterface;
  classNames?: string[];
}

interface ButtonZoneState {
  hoverActive: boolean;
}

export class ButtonZoneComponent extends React.PureComponent<ButtonZoneProps, ButtonZoneState> {
  public constructor(props: ButtonZoneProps) {
    super(props);
    this.state = { hoverActive: false };
  }

  private isEnabled = (): boolean => {
    return this.props.enabledState === DashboardServerButtonEnabledState.Enabled;
  };

  private isDisabledForPermissions = (): boolean => {
    return this.props.enabledState === DashboardServerButtonEnabledState.DisabledNoPermissions;
  };

  private handleDoubleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (this.props.isAuthoring && !this.props.isCurrentSheetStoryboard && event.button === 0 && !event.altKey) {
      this.logOpenButtonConfigDialogInvoked(ButtonZoneTelemetryConstants.DoubleMouseClickType);
      this.props.handleAuthoringDoubleClick();
    }
  };

  private handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const isValidPress = event.altKey || !this.props.isAuthoring;
    if (!isValidPress) {
      return;
    }

    const clickAction = event.altKey
      ? ButtonZoneTelemetryConstants.AltSingleMouseClickType
      : ButtonZoneTelemetryConstants.SingleMouseClickType;

    if (this.isEnabled()) {
      this.logButtonActionInvoked(clickAction);
      this.props.buttonClickAction();
    } else if (this.isDisabledForPermissions()) {
      this.logOpenButtonPermissionsErrorDialogInvoked(clickAction);
      this.props.handlePermissionsError();
    }
  };

  private handleKeyDown = (keyboardEvent: React.KeyboardEvent<HTMLDivElement>): void => {
    if (keyboardEvent.key !== KeyboardEventKey.Enter && keyboardEvent.key !== KeyboardEventKey.Space) {
      return;
    }

    if (this.isEnabled()) {
      this.logButtonActionInvoked(ButtonZoneTelemetryConstants.EnterSpaceKeyClickType);
      this.props.buttonClickAction();
    } else if (this.isDisabledForPermissions()) {
      this.logOpenButtonPermissionsErrorDialogInvoked(ButtonZoneTelemetryConstants.EnterSpaceKeyClickType);
      this.props.handlePermissionsError();
    }

    keyboardEvent.stopPropagation();
    keyboardEvent.preventDefault();
  };

  private handleMouseEnter = (): void => {
    this.setState({ hoverActive: true });
  };

  private handleMouseLeave = (): void => {
    this.setState({ hoverActive: false });
  };

  private createButtonStyle(): React.CSSProperties {
    const buttonBackgroundColor = this.isEnabled()
      ? ColorModel.fromColorCode(this.props.backgroundColor).toRGBA()
      : HexGrayscaleBackgroundColor;

    const style: React.CSSProperties = {
      backgroundColor: buttonBackgroundColor,
      width: this.props.contentWidth,
      height: this.props.contentHeight,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxSizing: 'border-box',
    };

    return { ...style, ...this.createBorderProperties() };
  }

  private createBorderProperties() {
    const isButtonTypeTextAndEnabledAndHoverActive =
      this.props.buttonType === DashboardButtonType.Text && this.isEnabled() && this.state.hoverActive;

    const borderProperties = isButtonTypeTextAndEnabledAndHoverActive
      ? this.createBorderPropertiesForHoverStateButtonText()
      : this.createDefaultBorderProperties();
    return borderProperties;
  }

  private createDefaultBorderProperties(): React.CSSProperties {
    const border = { ...this.props.border };
    return {
      borderColor: this.isEnabled() ? border.color : HexGrayscaleBorderColor,
      borderStyle: BorderDefinition.borderStyleToCss(border.style),
      borderWidth: border.width,
    };
  }

  private createBorderPropertiesForHoverStateButtonText(): React.CSSProperties {
    const normalBorderColor = this.props.border.hasColor()
      ? ColorModel.fromColorCode(this.props.border.color)
      : ColorModel.fromColorCode(this.props.backgroundColor);

    const overlayBorderColor = normalBorderColor.isLuminanceAboveThreshold(0.2)
      ? TextButtonHoverOverlayColorDark
      : TextButtonHoverOverlayColorLight;

    const finalTint = ColorModel.alphaBlend(overlayBorderColor, normalBorderColor);

    return {
      borderColor: finalTint.toRGBA(),
      borderStyle: BorderDefinition.borderStyleToCss(this.props.border.hasStyle() ? this.props.border.style : BorderStyle.Solid),
      borderWidth: this.props.border.hasWidth() ? this.props.border.width : DefaultHoverBorderWidth,
    };
  }

  public render(): JSX.Element {
    let buttonClassNames: string[] = [ButtonZoneStyle.tabButtonObject];
    if (this.props.classNames && this.props.classNames?.length > 0) {
      buttonClassNames = buttonClassNames.concat(...this.props.classNames);
    }

    return (
      <div
        role="button"
        tabIndex={0}
        className={buttonClassNames.join(' ')}
        data-testid={ButtonZoneComponentTestId}
        style={this.createButtonStyle()}
        title={this.props.buttonTooltip}
        aria-label={this.props.buttonTooltip}
        onClick={this.handleClick}
        onDoubleClick={this.handleDoubleClick}
        onKeyDown={this.handleKeyDown}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        {this.renderButtonType()}
        {!this.isEnabled() ? this.renderDisableOverlay() : null}
      </div>
    );
  }

  private renderButtonType(): JSX.Element {
    if (this.props.buttonType === DashboardButtonType.Text) {
      return this.renderButtonZoneText();
    } else {
      return this.renderButtonZoneImage();
    }
  }

  private renderButtonZoneText(): JSX.Element {
    const textContent = { ...this.props.textContent };

    return (
      <ButtonZoneTextComponent
        buttonTextIsEnabled={this.isEnabled()}
        effectiveCaption={textContent.effectiveCaption}
        captionTextFont={textContent.captionTextFont}
        captionTextColor={textContent.captionTextColor}
        captionFontSize={textContent.captionFontSize}
        captionFontIsBold={textContent.captionFontIsBold}
        captionFontIsItalic={textContent.captionFontIsItalic}
        captionFontIsUnderlined={textContent.captionFontIsUnderlined}
        handleFontLoaded={textContent.handleFontLoaded}
      />
    );
  }

  private renderButtonZoneImage(): JSX.Element {
    const imageContent = { ...this.props.imageContent };

    return (
      <ButtonZoneImageComponent
        imageUrl={imageContent.imageUrl}
        encodedImage={imageContent.encodedImage}
        encodedSVGImage={imageContent.encodedSVGImage}
        imageHeight={imageContent.imageHeight}
        imageWidth={imageContent.imageWidth}
        backgroundColor={this.props.backgroundColor}
        hoverActive={this.state.hoverActive}
        isButtonImageEnabled={this.isEnabled()}
        onImageLoadError={imageContent.onImageLoadError}
      />
    );
  }

  private renderDisableOverlay(): JSX.Element {
    return <div className={ButtonZoneStyle.tabButtonZoneDisabledOverlay} />;
  }

  private logButtonActionInvoked(clickAction: string): void {
    this.props.buttonZoneTelemetry.logButtonActionInvoked(
      this.props.isAuthoring,
      clickAction,
      this.props.buttonType,
      this.props.buttonBehaviour
    );
  }

  private logOpenButtonConfigDialogInvoked(clickAction: string): void {
    this.props.buttonZoneTelemetry.logOpenButtonConfigDialogInvoked(clickAction, this.props.buttonType, this.props.buttonBehaviour);
  }

  private logOpenButtonPermissionsErrorDialogInvoked(clickAction: string): void {
    this.props.buttonZoneTelemetry.logOpenButtonPermissionsErrorDialogInvoked(
      clickAction,
      this.props.buttonType,
      this.props.buttonBehaviour
    );
  }
}
